import type { BaseFreightGroup, BaseFreightRate, Ratebook } from '@api/useFreight/types'
import { useRateManagement } from '@api/useFreight/useFreight'
import { Modal } from 'antd'
import { useQueryClient } from '@tanstack/react-query'

import { type MutableRefObject, useEffect, useState } from 'react'
import type { GridApi } from 'ag-grid-community'

interface UseBaseFreightStateProps {
  ratebook?: Ratebook
  gridRef?: MutableRefObject<GridApi<BaseFreightGroup>>
}

interface UseBaseFreightStateReturn {
  baseFreightGroups: BaseFreightGroup[] | undefined
  selectedGroup: BaseFreightGroup | null
  isEditMode: boolean
  drawerVisible: boolean
  editingBaseFreight: BaseFreightGroup | null
  refetch: () => void
  setSelectedGroup: (group: BaseFreightGroup | null) => void
  setIsEditMode: (isEdit: boolean) => void
  openCreateDrawer: () => void
  openEditDrawer: (group: BaseFreightGroup) => void
  closeDrawer: () => void
  handleCreateSuccess: (created: BaseFreightGroup) => void
  exportBaseFreight: (ratebook?: Ratebook) => Promise<{ data: Blob }>
  importBaseFreight: (formData: FormData, ratebook?: Ratebook) => Promise<void>
}

export function useBaseFreightState({ ratebook, gridRef }: UseBaseFreightStateProps): UseBaseFreightStateReturn {
  const queryClient = useQueryClient()
  const { useBaseFreightQuery, exportBaseFreight, importBaseFreight } = useRateManagement()

  const { data: baseFreightRatesResponse, refetch } = useBaseFreightQuery(ratebook)

  const baseFreightGroups = baseFreightRatesResponse?.rows
  // State for the selected group and rate
  const [selectedGroup, setSelectedGroup] = useState<BaseFreightGroup | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingBaseFreight, setEditingBaseFreight] = useState<BaseFreightGroup | null>(null)

  const gridApi = gridRef?.current

  useEffect(() => {
    if (baseFreightGroups && baseFreightGroups.length > 0) {
      if (selectedGroup) {
        const responseMatch = baseFreightGroups.find((group) => group.key === selectedGroup.key)
        setSelectedGroup(responseMatch ?? null)
        if (gridApi && responseMatch?.key) {
          const row = gridApi.getRowNode(responseMatch.key)
          if (row) {
            row.setSelected(true)
            setSelectedGroup(responseMatch)
          }
        }
      } else {
        setSelectedGroup(baseFreightGroups[0])
      }
    }
  }, [baseFreightGroups, selectedGroup, gridApi])

  const handleCreateSuccess = (created: BaseFreightGroup) => {
    // Find the group that contains this rate
    const matchingGroup = baseFreightGroups?.find((baseFreightGroup) =>
      baseFreightGroup.rows.some((rate) => rate.key === created.key)
    )
    if (matchingGroup) {
      setSelectedGroup(matchingGroup)
    }
  }

  const openCreateDrawer = () => {
    setEditingBaseFreight(null)
    setDrawerVisible(true)
  }

  const openEditDrawer = (group: BaseFreightGroup) => {
    setEditingBaseFreight(group)
    setDrawerVisible(true)
  }

  const closeDrawer = () => {
    setDrawerVisible(false)
    setEditingBaseFreight(null)
  }

  const handleSelectChange = (newSelected: BaseFreightGroup | null) => {
    if (isEditMode && newSelected?.key !== selectedGroup?.key) {
      Modal.confirm({
        title: 'Discard Changes?',
        content: 'You are currently editing rate details. Selecting a new row will discard unsaved changes.',
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep Editing',
        onOk: () => {
          setIsEditMode(false)
          setSelectedGroup(newSelected)
          if (gridApi) {
            gridApi.refreshCells({ force: true })
          }
        },
        onCancel: () => {
          if (gridApi && selectedGroup?.key) {
            const previousNode = gridApi.getRowNode(selectedGroup.key)
            if (previousNode) {
              previousNode.setSelected(true)
            }
          }
        },
      })
    } else if (newSelected?.key !== selectedGroup?.key) {
      setSelectedGroup(newSelected)
      if (gridApi) {
        gridApi.refreshCells({ force: true })
      }
    }
  }

  return {
    baseFreightGroups,
    selectedGroup,
    isEditMode,
    drawerVisible,
    editingBaseFreight,
    refetch,
    setSelectedGroup: handleSelectChange,
    setIsEditMode,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateSuccess,
    exportBaseFreight,
    importBaseFreight,
  }
}
